/**
 * Default Access Policy Registry
 * 
 * Centralized configuration for all access policies in the application.
 * This can be loaded from a database, API, or configuration file in production.
 */

import {
  AccessPolicyRegistry,
  AccessPolicy,
  RouteAccessPolicy,
  Role,
  Permission
} from '../types/accessControl';

// Define access policies
const accessPolicies: AccessPolicy[] = [
  // Super Admin Policy
  {
    id: 'super_admin_full_access',
    name: 'Super Admin Full Access',
    description: 'Super administrators have full access to all resources',
    roles: ['super_admin'],
    priority: 100,
    active: true
  },

  // Backoffice Security Policies
  {
    id: 'backoffice_policy_admin',
    name: 'Backoffice Policy Administrator',
    description: 'Policy administrators can manage access policies',
    roles: ['policy_admin'],
    permissions: ['policy:read', 'policy:write', 'policy:delete', 'backoffice:access'],
    permissionMode: 'all',
    priority: 95,
    active: true
  },

  {
    id: 'backoffice_auditor',
    name: 'Backoffice Auditor',
    description: 'Auditors can view and export audit logs',
    roles: ['auditor'],
    permissions: ['audit:read', 'audit:export', 'backoffice:access'],
    permissionMode: 'all',
    priority: 90,
    active: true
  },

  {
    id: 'backoffice_system_admin',
    name: 'Backoffice System Administrator',
    description: 'System administrators have full backoffice access',
    roles: ['system_admin'],
    permissions: ['policy:read', 'policy:write', 'policy:delete', 'policy:approve', 'policy:rollback', 'audit:read', 'audit:export', 'backoffice:access', 'backoffice:admin'],
    permissionMode: 'all',
    priority: 98,
    active: true
  },

  {
    id: 'backoffice_mfa_required',
    name: 'Backoffice MFA Requirement',
    description: 'All backoffice access requires MFA verification',
    conditions: [
      {
        attribute: 'mfaEnabled',
        operator: 'equals',
        value: true
      }
    ],
    priority: 85,
    active: true
  },

  // Admin Policies
  {
    id: 'admin_user_management',
    name: 'Admin User Management',
    description: 'Administrators can manage users',
    roles: ['admin'],
    permissions: ['user:read', 'user:write', 'user:delete'],
    permissionMode: 'all',
    priority: 90,
    active: true
  },

  {
    id: 'admin_content_management',
    name: 'Admin Content Management',
    description: 'Administrators can manage all content',
    roles: ['admin'],
    permissions: ['content:read', 'content:write', 'content:delete', 'content:publish'],
    permissionMode: 'all',
    priority: 90,
    active: true
  },

  {
    id: 'admin_reports_access',
    name: 'Admin Reports Access',
    description: 'Administrators can access all reports',
    roles: ['admin'],
    permissions: ['reports:read', 'reports:write', 'reports:export'],
    permissionMode: 'all',
    priority: 90,
    active: true
  },

  // Manager Policies
  {
    id: 'manager_user_read',
    name: 'Manager User Read Access',
    description: 'Managers can read user information',
    roles: ['manager'],
    permissions: ['user:read'],
    priority: 80,
    active: true
  },

  {
    id: 'manager_content_management',
    name: 'Manager Content Management',
    description: 'Managers can manage content',
    roles: ['manager'],
    permissions: ['content:read', 'content:write'],
    permissionMode: 'all',
    priority: 80,
    active: true
  },

  {
    id: 'manager_reports_read',
    name: 'Manager Reports Read',
    description: 'Managers can read reports',
    roles: ['manager'],
    permissions: ['reports:read'],
    priority: 80,
    active: true
  },

  // Instructor Policies
  {
    id: 'instructor_course_management',
    name: 'Instructor Course Management',
    description: 'Instructors can manage courses and students',
    roles: ['instructor'],
    permissions: ['course:read', 'course:write', 'student:read', 'student:write'],
    permissionMode: 'all',
    priority: 70,
    active: true
  },

  {
    id: 'instructor_content_access',
    name: 'Instructor Content Access',
    description: 'Instructors can access and create content',
    roles: ['instructor'],
    permissions: ['content:read', 'content:write'],
    permissionMode: 'all',
    priority: 70,
    active: true
  },

  // Coordinator Policies
  {
    id: 'coordinator_event_management',
    name: 'Coordinator Event Management',
    description: 'Coordinators can manage events and students',
    roles: ['coordinator'],
    permissions: ['event:read', 'event:write', 'student:read', 'student:write'],
    permissionMode: 'all',
    priority: 60,
    active: true
  },

  {
    id: 'coordinator_content_read',
    name: 'Coordinator Content Read',
    description: 'Coordinators can read content',
    roles: ['coordinator'],
    permissions: ['content:read'],
    priority: 60,
    active: true
  },

  // Supervisor Policies
  {
    id: 'supervisor_member_management',
    name: 'Supervisor Member Management',
    description: 'Supervisors can manage members',
    roles: ['supervisor'],
    permissions: ['member:read', 'member:write'],
    permissionMode: 'all',
    priority: 50,
    active: true
  },

  {
    id: 'supervisor_reports_read',
    name: 'Supervisor Reports Read',
    description: 'Supervisors can read reports',
    roles: ['supervisor'],
    permissions: ['reports:read'],
    priority: 50,
    active: true
  },

  // User Policies
  {
    id: 'user_profile_management',
    name: 'User Profile Management',
    description: 'Users can manage their own profile',
    roles: ['user'],
    permissions: ['profile:read', 'profile:write'],
    permissionMode: 'all',
    priority: 30,
    active: true
  },

  {
    id: 'user_content_read',
    name: 'User Content Read',
    description: 'Users can read content',
    roles: ['user'],
    permissions: ['content:read'],
    priority: 30,
    active: true
  },

  // Guest Policies
  {
    id: 'guest_content_read',
    name: 'Guest Content Read',
    description: 'Guests can read public content',
    roles: ['guest'],
    permissions: ['content:read'],
    priority: 10,
    active: true
  },

  // ABAC Policies (Attribute-Based Access Control)
  {
    id: 'finance_department_reports',
    name: 'Finance Department Reports',
    description: 'Only finance department members can access financial reports',
    permissions: ['reports:read'],
    conditions: [
      {
        attribute: 'department',
        operator: 'equals',
        value: 'finance'
      }
    ],
    priority: 85,
    active: true
  },

  {
    id: 'high_clearance_sensitive_data',
    name: 'High Clearance Sensitive Data',
    description: 'Only users with high clearance can access sensitive data',
    permissions: ['admin:read'],
    conditions: [
      {
        attribute: 'clearanceLevel',
        operator: 'greater_than',
        value: 5
      }
    ],
    priority: 95,
    active: true
  },

  {
    id: 'active_user_requirement',
    name: 'Active User Requirement',
    description: 'Only active users can access protected resources',
    conditions: [
      {
        attribute: 'isActive',
        operator: 'equals',
        value: true
      }
    ],
    priority: 20,
    active: true
  },

  // Additional ABAC Policies for Enterprise Use Cases
  {
    id: 'time_based_access',
    name: 'Time-Based Access Control',
    description: 'Restrict access during business hours only',
    conditions: [
      {
        attribute: 'lastLogin',
        operator: 'greater_than',
        value: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() // Last 24 hours
      }
    ],
    priority: 15,
    active: true
  },

  {
    id: 'location_based_access',
    name: 'Location-Based Access Control',
    description: 'Restrict access based on user location',
    conditions: [
      {
        attribute: 'location',
        operator: 'in',
        value: ['US', 'CA', 'MX'] // North American locations
      }
    ],
    priority: 25,
    active: true
  },

  {
    id: 'department_specific_content',
    name: 'Department-Specific Content Access',
    description: 'Users can only access content from their department',
    conditions: [
      {
        attribute: 'department',
        operator: 'exists',
        value: true
      }
    ],
    priority: 35,
    active: true
  },

  {
    id: 'high_clearance_admin_access',
    name: 'High Clearance Admin Access',
    description: 'Admin access requires high clearance level',
    roles: ['admin'],
    conditions: [
      {
        attribute: 'clearanceLevel',
        operator: 'greater_than',
        value: 3
      }
    ],
    priority: 95,
    active: true
  },

  {
    id: 'multi_factor_authentication_required',
    name: 'Multi-Factor Authentication Required',
    description: 'Sensitive operations require MFA',
    conditions: [
      {
        attribute: 'mfaEnabled',
        operator: 'equals',
        value: true
      }
    ],
    priority: 40,
    active: true
  }
];

// Define route-specific policies
const routePolicies: RouteAccessPolicy[] = [
  // Admin Routes
  {
    route: '/admin/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'super_admin_full_access')!,
      accessPolicies.find(p => p.id === 'admin_user_management')!,
      accessPolicies.find(p => p.id === 'admin_content_management')!,
      accessPolicies.find(p => p.id === 'admin_reports_access')!
    ],
    defaultAction: 'deny'
  },

  // User Management Routes
  {
    route: '/admin/users',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'admin_user_management')!,
      accessPolicies.find(p => p.id === 'manager_user_read')!
    ],
    defaultAction: 'deny'
  },

  // Content Management Routes
  {
    route: '/admin/content',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'admin_content_management')!,
      accessPolicies.find(p => p.id === 'manager_content_management')!,
      accessPolicies.find(p => p.id === 'instructor_content_access')!
    ],
    defaultAction: 'deny'
  },

  // Reports Routes
  {
    route: '/reports/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'admin_reports_access')!,
      accessPolicies.find(p => p.id === 'manager_reports_read')!,
      accessPolicies.find(p => p.id === 'supervisor_reports_read')!,
      accessPolicies.find(p => p.id === 'finance_department_reports')!
    ],
    defaultAction: 'deny'
  },

  // Course Management Routes
  {
    route: '/courses/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'instructor_course_management')!,
      accessPolicies.find(p => p.id === 'coordinator_event_management')!
    ],
    defaultAction: 'deny'
  },

  // Event Management Routes
  {
    route: '/events/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'coordinator_event_management')!,
      accessPolicies.find(p => p.id === 'instructor_course_management')!
    ],
    defaultAction: 'deny'
  },

  // Member Management Routes
  {
    route: '/supervisor/members/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'supervisor_member_management')!
    ],
    defaultAction: 'deny'
  },

  // Profile Routes
  {
    route: '/profile/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'user_profile_management')!
    ],
    defaultAction: 'deny'
  },

  // Dashboard Routes
  {
    route: '/dashboard/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'active_user_requirement')!
    ],
    defaultAction: 'deny'
  },

  // Public Routes (no authentication required)
  {
    route: '/',
    requireAuth: false,
    policies: [],
    defaultAction: 'allow'
  },

  {
    route: '/alerts',
    requireAuth: false,
    policies: [],
    defaultAction: 'allow'
  },

  {
    route: '/about',
    requireAuth: false,
    policies: [],
    defaultAction: 'allow'
  },

  {
    route: '/contact',
    requireAuth: false,
    policies: [],
    defaultAction: 'allow'
  },

  {
    route: '/travel',
    requireAuth: false,
    policies: [],
    defaultAction: 'allow'
  },

  {
    route: '/login',
    requireAuth: false,
    policies: [],
    defaultAction: 'allow'
  },

  {
    route: '/register',
    requireAuth: false,
    policies: [],
    defaultAction: 'allow'
  },

  // Backoffice Routes
  {
    route: '/backoffice/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'backoffice_system_admin')!,
      accessPolicies.find(p => p.id === 'backoffice_policy_admin')!,
      accessPolicies.find(p => p.id === 'backoffice_auditor')!,
      accessPolicies.find(p => p.id === 'backoffice_mfa_required')!
    ],
    defaultAction: 'deny'
  },

  {
    route: '/backoffice/policies/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'backoffice_system_admin')!,
      accessPolicies.find(p => p.id === 'backoffice_policy_admin')!
    ],
    defaultAction: 'deny'
  },

  {
    route: '/backoffice/audit/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'backoffice_system_admin')!,
      accessPolicies.find(p => p.id === 'backoffice_auditor')!
    ],
    defaultAction: 'deny'
  },

  {
    route: '/backoffice/admin/*',
    requireAuth: true,
    policies: [
      accessPolicies.find(p => p.id === 'backoffice_system_admin')!
    ],
    defaultAction: 'deny'
  }
];

// Create the policy registry
export const defaultAccessPolicyRegistry: AccessPolicyRegistry = {
  global: accessPolicies,
  routes: routePolicies,
  defaults: {
    requireAuth: true,
    defaultAction: 'deny',
    fallbackPolicy: {
      id: 'default_deny',
      name: 'Default Deny',
      description: 'Default policy that denies access',
      priority: 0,
      active: true
    }
  }
};

// Helper function to get policy by ID
export const getPolicyById = (id: string): AccessPolicy | undefined => {
  return accessPolicies.find(policy => policy.id === id);
};

// Helper function to get policies by role
export const getPoliciesByRole = (role: Role): AccessPolicy[] => {
  return accessPolicies.filter(policy => 
    policy.roles?.includes(role)
  );
};

// Helper function to get policies by permission
export const getPoliciesByPermission = (permission: Permission): AccessPolicy[] => {
  return accessPolicies.filter(policy => 
    policy.permissions?.includes(permission)
  );
};

// Helper function to get route policy using improved matching
export const getRoutePolicy = (route: string): RouteAccessPolicy | undefined => {
  return routePolicies.find(routePolicy => {
    // Exact match
    if (routePolicy.route === route) {
      return true;
    }
    
    // Wildcard match (legacy support)
    if (routePolicy.route.endsWith('*')) {
      const prefix = routePolicy.route.slice(0, -1);
      return route.startsWith(prefix);
    }
    
    // Path-to-regexp style matching
    try {
      const { match } = require('path-to-regexp');
      const matcher = match(routePolicy.route, { decode: decodeURIComponent });
      return matcher(route);
    } catch (error) {
      // Fallback to simple string matching if path-to-regexp fails
      return false;
    }
  });
};

export default defaultAccessPolicyRegistry;
